import { useEffect, useState, useMemo, useRef, Suspense, lazy } from 'react';
import { useScrollStore } from '../../navigation/store/scrollStore';
// import Section from '../../../layouts/Section'; // Static import removed
import { useSharedIntersectionObserver } from 'shared/hooks';
import { calculateSectionHeight, getFallbackHeight } from '../utils/sectionHeightCalculator';
import SectionErrorBoundary from './SectionErrorBoundary';
import { SCROLL_THRESHOLDS } from '../../navigation/constants/scrollConstants';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import styles from './LazySectionWrapper.module.css';

// Lazy load the Section component
const Section = lazy(() => import('../../../layouts/Section'));

interface LazySectionWrapperProps {
  section: ISectionSettings;
  isPortrait: boolean;
  index: number;
  shouldPreload: boolean;
}

// Lazy Section Component with Intersection Observer
const LazySectionWrapper: React.FC<LazySectionWrapperProps> = ({ section, isPortrait, index, shouldPreload }) => {
  const { elementRef, isVisible } = useSharedIntersectionObserver(true, shouldPreload);
  const hasJackpotPanel = !!section.jackpotName;
  const [hasLoaded, setHasLoaded] = useState(shouldPreload);
  const [showContent, setShowContent] = useState(shouldPreload);

  // Access scroll store to get/set section height
  const setSectionHeight = useScrollStore((state) => state.setSectionHeight);
  const getSectionHeight = useScrollStore((state) => state.getSectionHeight);

  // Get stored height if available
  const storedHeight = useMemo(
    () => getSectionHeight(section.name),
    [section.name, getSectionHeight]
  );

  // Calculate placeholder height based on section type and content
  const placeholderHeight = useMemo(() => {
    // Priority: Stored Height > Calculated Height > Fallback
    if (storedHeight && storedHeight > 0) {
      return storedHeight;
    }

    try {
      return calculateSectionHeight({
        sectionName: section.name,
        gamesCount: section.games?.length || 0,
        hasJackpotPanel,
        viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
      });
    } catch (error) {
      console.warn('Failed to calculate section height, using fallback', error);
      return getFallbackHeight(section.name);
    }
    // `isPortrait` is not read by the estimate, but window.innerWidth is - and that isn't
    // reactive, so an orientation flip is the only signal to measure again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.name, section.games?.length, hasJackpotPanel, isPortrait, storedHeight]);

  // Load section when it becomes visible or should be preloaded
  useEffect(() => {
    if (isVisible && !hasLoaded) {
      setHasLoaded(true);
      // Small delay to ensure smooth animation trigger
      requestAnimationFrame(() => {
        setShowContent(true);
      });
    }
  }, [isVisible, hasLoaded, section?.name, index]);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Update stored height when content is loaded using ResizeObserver
  useEffect(() => {
    if (showContent && elementRef.current) {
      let lastHeight = storedHeight || 0;
      let stabilityCounter = 0;
      const STABILITY_THRESHOLD = SCROLL_THRESHOLDS.RESIZE_STABILITY_COUNT;

      // Create ResizeObserver to track height changes
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;

          // Only update if height has changed significantly
          if (Math.abs(height - lastHeight) > SCROLL_THRESHOLDS.HEIGHT_CHANGE_THRESHOLD) {
            stabilityCounter = 0;
            lastHeight = height;

            if (height > 0) {
              setSectionHeight(section.name, height);
            }
          } else {
            // Height is stable, increment counter
            stabilityCounter++;

            // After consistent measurements, we can disconnect
            if (stabilityCounter >= STABILITY_THRESHOLD && resizeObserverRef.current) {
              if (lastHeight > 0) {
                setSectionHeight(section.name, lastHeight);
              }
              resizeObserverRef.current.disconnect();
              resizeObserverRef.current = null;
            }
          }
        }
      });

      resizeObserverRef.current.observe(elementRef.current);

      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      };
    }
    return undefined;
  }, [showContent, section.name, setSectionHeight, storedHeight, elementRef]);

  return (
    <div
      ref={elementRef}
      className={styles.sectionPlaceholder}
      style={{ minHeight: showContent ? 'auto' : `${placeholderHeight}px` }}
      data-section-index={index}
      data-section-name={section.name}
    >
      {/* Optional: Show subtle loading shimmer while loading */}
      {!showContent && <div className={styles.loadingPlaceholder} />}

      {hasLoaded ? (
        <div className={`${styles.sectionContent} ${shouldPreload ? styles.preloaded : ''}`}>
          <SectionErrorBoundary sectionName={section.name}>
            <Suspense fallback={<div style={{ height: placeholderHeight }} />}>
              <Section
                key={section.name}
                data={section}
                isPortrait={isPortrait}
              />
            </Suspense>
          </SectionErrorBoundary>
        </div>
      ) : null}
    </div>
  );
};

export default LazySectionWrapper;
